import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-kollege-v-podarok")!;

const faqItems = [
  { q: "На какой повод подойдёт песня коллеге?", a: "На день рождения, юбилей работы, повышение, уход на новую должность или просто как сюрприз от отдела." },
  { q: "Можно ли упомянуть рабочие моменты в тексте?", a: "Именно это и делает песню особенной. Расскажите про планёрки, дедлайны, корпоративные шутки." },
  { q: "Это не будет выглядеть несерьёзно?", a: "Наоборот. Это признание заслуг человека в нестандартной форме." },
  { q: "Можно ли включить песню на корпоративе?", a: "Конечно. Файл скачивается в высоком качестве." },
  { q: "Сколько стоит песня для коллеги?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const PesnyaKollegeVPodarok = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня коллеге в подарок</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Одиннадцать вечера. Завтра у Марины Петровны юбилей — пятнадцать лет в компании. Она заслуживает чего-то большего, чем торт и сертификат.</p>
            <p>Вы пишете историю. Через три минуты — <strong className="text-foreground">песня, в которой всё это звучит</strong>.</p>
            <p><strong className="text-foreground">Персональная песня коллеге</strong> — настоящий трек, написанный по вашей истории.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — самый запоминающийся корпоративный подарок</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Сертификаты забываются. А песня с именем коллеги и историями из офисной жизни — будет пересылаться в рабочем чате годами.</p>
              <p>О подарках для близких — <Link to="/pesnya-v-podarok" className="text-primary hover:underline">песня в подарок</Link>.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">💼 Создать песню для коллеги</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Когда дарить песню на работе</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground"><Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">День рождения</Link>.</strong> Включите трек на планёрке. Три минуты, от которых весь офис в восторге.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-na-yubiley" className="text-primary hover:underline">Юбилей</Link> работы.</strong> Расскажите про путь коллеги от стажёра до руководителя.</p>
              <p><strong className="text-foreground">Корпоратив.</strong> Любой повод — Новый год, 23 февраля, 8 Марта.</p>
              <p><strong className="text-foreground">Проводы.</strong> Трек с лучшими моментами совместной работы.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Директор переслал песню в общий чат с подписью: лучший тимбилдинг в истории компании»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>HR-менеджер Катя собрала истории со всего отдела и создала весёлый поп-трек. На корпоративе руководитель услышал своё имя — и сказал: «За десять лет — это первый подарок, который я реально запомню».</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Удивить коллегу так, чтобы обсуждали неделю</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о песнях для коллег</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть работа зазвучит по-новому</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте подарок, о котором будут говорить на каждом корпоративе.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaKollegeVPodarok;
